import React, { useState, useEffect } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import {
  TableHead,
  Typography,
  Button,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
  DialogContentText,
  Box,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  FormControl,
  Select,
  MenuItem,
  TableRow,
  Paper,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Stack,
  Pagination,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  saveVehicleAction,
  VehicleUpdateAction,
  getVehicleInfoByVIN,
} from "../../../redux/actions/vehicleInfoActions";
import { useDispatch, useSelector } from "react-redux";
import {
  getDetailPolicyAction,
  getPolicyPremiumDetails,
  getDetailPolicyPaginationAction,
  getCoverageListingAction,
  deleteVechileAction
} from "../../../redux/actions/policyActions";
import {
  coll,
  liabbi_limit2,
  liabbi_limit1,
  liabpd,
  umbi_limit1,
  umbi_limit2,
  comp,
  pipDed,
  medPay,
  towing,
  Rentals,
} from "../../../common/StaticFieldList";
import {
  getVehicleInfoActionByYmsYears,
  getVehicleInfoActionByYmsModels,
  getVehicleInfoActionByYmsMakes,
  getVehicleInfoActionByYmsVins,
  getVehicleInfoActionByYmsStyles,
  getVehicleInfoActionByYmsVin,
} from "../../../redux/actions/vehicleInfoActions";
import PolicyVechileDetails from "./PolicyVechileDetails";

const NO_COVG_VALUE = "__NO_COVG__";
const STATE_MIN_VALUE = "__STATE_MIN__";

const LIMIT_TYPE = {
  STATE_MIN: "STATE_MINIMUM",
  SPECIFIC: "SPECIFIC_VALUE",
  NO_COVERAGE: "NO_COVERAGE",
};

const normalizeKey = (s) =>
  String(s ?? "").trim().toUpperCase().replace(/[\s_]/g, "");
const norm = (s) => String(s ?? "").trim().toUpperCase();

const BI_STATE_MINIMUM = {
  FL: { limit1: 10000, limit2: 20000 },
  GA: { limit1: 25000, limit2: 50000 },
  TX: { limit1: 30000, limit2: 60000 },
  NC: { limit1: 30000, limit2: 60000 },
};

const STATE_ALIASES = {
  FLORIDA: "FL",
  GEORGIA: "GA",
  TEXAS: "TX",
  "NORTH CAROLINA": "NC",
};

const getStateCode = (policyState) => {
  const raw = (policyState || "").toString().trim().toUpperCase();
  return STATE_ALIASES[raw] || raw;
};

const isLimitTypeStateMin = (lt) => {
  const v = norm(lt).replace(/\s+/g, "");
  return v === "STATE_MIN_VALUE" || v === "STATE_MINIMUM";
};

const isLimitTypeNoCov = (lt) => {
  const v = norm(lt).replace(/[\s_]/g, "");
  return v === "NOCOVERAGE";
};

const isStateMinimum = (v) => {
  if (v === STATE_MIN_VALUE) return true;
  const n = norm(v).replace(/[\s_]/g, "");
  return n === "STATEMINIMUM" || n === "STATEMIN";
};

const isNoCovg = (v) => {
  if (v == null) return false;
  if (v === NO_COVG_VALUE) return true;
  const k = normalizeKey(v);
  return (
    k === "NA" ||
    k === "N/A" ||
    k === "NONE" ||
    k === "NO" ||
    k === "NOCOVG" ||
    k === "NOCOVERAGE"
  );
};

const hasAtLeastOneRealCoverage = (policyState, covs) => {
  const normalized = normalizeCoverages(policyState, covs);
  return normalized.some(
    (c) => String(c.type || "").trim() !== "" && c.limitType !== LIMIT_TYPE.NO_COVERAGE
  );
};


const isStateMinToken = (s) => {
  const k = String(s ?? "").trim().toUpperCase().replace(/[\s_]/g, "");
  return (
    k === "STATEMIN" ||
    k === "STATEMINIMUM" ||
    k === "STATEMINVALUE" ||
    k === "__STATEMIN__"
  );
};
const isNoCovgToken = (s) => {
  const k = String(s ?? "").trim().toUpperCase().replace(/[\s_]/g, "");
  return k === "NOCOVG" || k === "NOCOVERAGE" || k === "NONE" || k === "NA" || k === "N/A";
};

const normalizeSpecialOption = (o = {}) => {
  const rawVal = o.value ?? o.label ?? "";
  const rawLbl = o.label ?? o.value ?? "";
  if (isNoCovgToken(rawVal) || isNoCovgToken(rawLbl)) {
    return { value: NO_COVG_VALUE, label: "No Covg" };
  }
  if (isStateMinToken(rawVal) || isStateMinToken(rawLbl)) {
    return { value: STATE_MIN_VALUE, label: "State Minimum" };
  }
  return { value: String(rawVal), label: String(rawLbl) };
};

const dedupeOptions = (opts = []) => {
  const seen = new Set();
  return (opts || []).filter((o) => {
    const key = String(o.value ?? "").trim().toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildOptions = (opts = [], includeStateMin = false) => {
  let list = (opts || []).map(normalizeSpecialOption);
  if (!list.some((o) => o.value === NO_COVG_VALUE)) {
    list = [{ value: NO_COVG_VALUE, label: "No Covg" }, ...list];
  }
  if (includeStateMin && !list.some((o) => o.value === STATE_MIN_VALUE)) {
    list = [{ value: STATE_MIN_VALUE, label: "State Minimum" }, ...list];
  }
  return dedupeOptions(list);
};

const inflateCoverageForUI = (policyState, cov) => {
  const c = { ...cov, type: String(cov.type || "").toLowerCase() };

  if (isLimitTypeNoCov(c.limitType)) {
    if (["bi", "umbi"].includes(c.type)) {
      c.limit1 = NO_COVG_VALUE;
      c.limit2 = NO_COVG_VALUE;
    } else if (["pd", "pip", "medpay","tow", "rental"].includes(c.type)) {
      c.limit1 = NO_COVG_VALUE;
      c.limit2 = null;
    } else if (["comp", "coll"].includes(c.type)) {
      c.deductible = NO_COVG_VALUE;
    }
    return c;
  }

  if (isLimitTypeStateMin(c.limitType)) {
    if (["bi", "umbi"].includes(c.type)) {
      c.limit1 = STATE_MIN_VALUE;
      c.limit2 = STATE_MIN_VALUE;
    } else if (["pd", "pip"].includes(c.type)) {
      c.limit1 = STATE_MIN_VALUE;
    }
  }

  if (["comp", "coll"].includes(c.type)) {
    if (String(c.deductible) === "0") c.deductible = NO_COVG_VALUE;
    return c;
  }

  if (["pd", "pip", "medpay", "tow", "rental"].includes(c.type)) {
    if (String(c.limit1) === "0") c.limit1 = NO_COVG_VALUE;
    return c;
  }

  if (["bi", "umbi"].includes(c.type)) {
    const l1 = String(c.limit1 ?? "");
    const l2 = String(c.limit2 ?? "");
    if (l1 === "0" && l2 === "0") {
      c.limit1 = NO_COVG_VALUE;
      c.limit2 = NO_COVG_VALUE;
    }
    return c;
  }

  return c;
};

const coverageHasNoCovg = (c) => {
  const t = (c?.type || "").toLowerCase();
  if (["comp", "coll"].includes(t)) return isNoCovg(c.deductible);
  if (["pd", "pip", "medpay","tow", "rental"].includes(t)) return isNoCovg(c.limit1);
  if (["bi", "umbi"].includes(t)) return isNoCovg(c.limit1) || isNoCovg(c.limit2);
  return false;
};

const normalizeCoverages = (policyState, covs) => {
  const st = getStateCode(policyState);
  const map = BI_STATE_MINIMUM[st];

  return covs.map((c) => {
    const t = (c.type || "").toLowerCase();
    let limitType = LIMIT_TYPE.SPECIFIC;

    if (["comp", "coll"].includes(t)) {
      if (isNoCovg(c.deductible)) {
        return { ...c, deductible: null, limit1: null, limit2: null, limitType: LIMIT_TYPE.NO_COVERAGE, premium: 0 };
      }
      return { ...c, limitType };
    }

    if (["pd", "pip", "medpay","tow", "rental"].includes(t)) {
      if (isNoCovg(c.limit1)) {
        return { ...c, limit1: null, limit2: null, deductible: null, limitType: LIMIT_TYPE.NO_COVERAGE, premium: 0 };
      }
      if (isStateMinimum(c.limit1)) {
        return {
          ...c,
          limit1: map ? String(map.limit1) : null,
          limit2: null,
          deductible: null,
          limitType: LIMIT_TYPE.STATE_MIN,
        };
      }
      return { ...c, limit2: null, deductible: null, limitType };
    }

    if (["bi", "umbi"].includes(t)) {
      const l1No = isNoCovg(c.limit1);
      const l2No = isNoCovg(c.limit2);
      if (l1No || l2No) {
        return { ...c, limit1: null, limit2: null, deductible: null, limitType: LIMIT_TYPE.NO_COVERAGE, premium: 0 };
      }
      if (isStateMinimum(c.limit1) || isStateMinimum(c.limit2)) {
        return {
          ...c,
          limit1: map ? String(map.limit1) : null,
          limit2: map ? String(map.limit2) : null,
          deductible: null,
          limitType: LIMIT_TYPE.STATE_MIN,
        };
      }
      return { ...c, deductible: null, limitType };
    }

    return { ...c, limitType };
  });
};

const normType = (t) => String(t || "").trim().toLowerCase();
const hasPipMedpayConflict = (list) => {
  const types = list.map((c) => normType(c.type));
  return types.includes("pip") && types.includes("medpay");
};

const ALL_TYPES = ["bi", "pd", "comp", "coll", "umbi", "pip", "medpay", "tow", "rental"];

const getUsedTypes = (coverages, excludeIndex = -1) =>
  coverages.map((c, i) => (i === excludeIndex ? "" : normType(c.type))).filter(Boolean);

const getTypeMenuOptions = (coverages, rowIndex) => {
  const currentType = normType(coverages[rowIndex]?.type);
  const usedElsewhere = new Set(getUsedTypes(coverages, rowIndex));
  const pipUsed = usedElsewhere.has("pip");
  const medpayUsed = usedElsewhere.has("medpay");

  return ALL_TYPES.map((t) => {
    let disabled = false;
    if (usedElsewhere.has(t) && t !== currentType) disabled = true;
    if (t === "pip" && medpayUsed && currentType !== "pip") disabled = true;
    if (t === "medpay" && pipUsed && currentType !== "medpay") disabled = true;
    return { value: t, disabled };
  });
};

const getRemainingTypesForNewRow = (coverages) => {
  const used = new Set(getUsedTypes(coverages, -1));
  let allowed = ALL_TYPES.filter((t) => !used.has(t));
  if (used.has("pip")) allowed = allowed.filter((t) => t !== "medpay");
  if (used.has("medpay")) allowed = allowed.filter((t) => t !== "pip");
  return allowed;
};


const PolicyVehicle = ({ policyId, driverId }) => {
  const [vehicleModel, setVechicleModel] = useState(false);
  const [getVehicleDetail, setVehicleDetail] = useState(null);
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [vehiclesPagination, setVehiclesPagination] = useState(null);

  const customerDetailsFromRedux = useSelector(
    (state) => state?.customerDetails?.selectedCustomer || null
  );
  const customerData = useSelector((state) => state.policy.allDetailPolicy);
  const policyState = customerData.state || null;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const getvehicleid = useSelector((state) => state.policy.allDetailPolicy.id);
  const years = useSelector((state) => state.vehicleInfo.years);
  const Make = useSelector((state) => state.vehicleInfo.makes);
  const models = useSelector((state) => state.vehicleInfo.models);
  const vehBodyType = useSelector((state) => state.vehicleInfo.styles);
  const vins = useSelector((state) => state.vehicleInfo.vins);
  const [deletedVechile, setDeletedVehicle] = useState(null);
  const [selectedOption, setSelectedOption] = useState("vin");
  const [open, setOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [page, setPage] = useState(0);
  const vehiclesPageObj = customerData?.vehicles || null; // { content, number, size, totalPages, totalElements, ...}
  const rows = vehiclesPageObj?.content ?? [];
  const totalElements = vehiclesPageObj?.totalElements ?? 0;
  const backendTotalPages = vehiclesPageObj?.totalPages ?? 0;
  const backendPage0 = vehiclesPageObj?.number ?? 0;  // 0-based from backend
  const currentPage1 = backendPage0 + 1;              // 1-based for MUI Pagination
  const backendSize = vehiclesPageObj?.size ?? 5;

  // UI size selector only (we’ll refetch on change)
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [garagingZipCode, setGaragingZipCode] = useState(
    customerDetailsFromRedux?.zipCode || null
  );

  const [coverageError, setCoverageError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [rowErrors, setRowErrors] = useState({});

  const [formDatas, setFormDatas] = useState({
    modelYear: "",
    manufacturer: "",
    model: "",
    vehBodyType: "",
    vin: "",
    zipcode: "",
    garaging: "",
  });

  const initialVehicleState = {
    vin: "",
    year: "",
    make: "",
    model: "",
    bodyType: "",
    garaging: "",
  };

  const [newVehicle, setNewVehicle] = useState(initialVehicleState);
  const [coverages, setCoverages] = useState([
    { type: "", limit1: null, limit2: null, deductible: "", premium: null },
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormDatas((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const limitOptions = {
    bi: { limit1: liabbi_limit1, limit2: liabbi_limit2 },
    pd: { limit1: liabpd },
    comp: { deductible: comp },
    coll: { deductible: coll },
    umbi: { limit1: umbi_limit1, limit2: umbi_limit2 },
    pip: { limit1: pipDed },
    medpay: { limit1: medPay },
    tow: { limit1: towing },
    rental: { limit1: Rentals },
  };

  const callbackfn = (res) => {
    if (res) {
      setFormDatas((prev) => ({ ...prev, engineType: res.engine || "" }));
    }
  };

  useEffect(() => {
  if (!policyId) return;
  // Ask backend for vehicles page 0 with the current rowsPerPage
  dispatch(getDetailPolicyPaginationAction(policyId, { section: 'vehicle', page: 0, size: rowsPerPage }));
}, [policyId, rowsPerPage, dispatch]);


  useEffect(() => {
    if (customerDetailsFromRedux?.zipCode) {
      setGaragingZipCode(customerDetailsFromRedux.zipCode);
    }
  }, [customerDetailsFromRedux]);

  useEffect(() => {
    dispatch(getVehicleInfoActionByYmsYears());
  }, [dispatch]);

  useEffect(() => {
    if (formDatas.modelYear) dispatch(getVehicleInfoActionByYmsMakes(formDatas.modelYear));
  }, [formDatas.modelYear, editMode, dispatch]);

  useEffect(() => {
    if (formDatas.manufacturer) {
      dispatch(
        getVehicleInfoActionByYmsModels(formDatas.modelYear, formDatas.manufacturer)
      );
    }
  }, [formDatas.manufacturer, formDatas.modelYear, editMode, dispatch]);

  useEffect(() => {
    if (formDatas.model) {
      dispatch(
        getVehicleInfoActionByYmsStyles(
          formDatas.modelYear,
          formDatas.manufacturer,
          formDatas.model
        )
      );
    }
  }, [formDatas.model, formDatas.manufacturer, formDatas.modelYear, editMode, dispatch]);

  useEffect(() => {
    if (formDatas.vehBodyType) {
      dispatch(
        getVehicleInfoActionByYmsVins(
          formDatas.modelYear,
          formDatas.manufacturer,
          formDatas.model,
          formDatas.vehBodyType
        )
      );
    }
  }, [formDatas.vehBodyType, formDatas.model, formDatas.manufacturer, formDatas.modelYear, dispatch]);

  useEffect(() => {
    if (selectedOption === "yms" && formDatas.vin) {
      const encodedVin = encodeURIComponent(formDatas.vin.trim());
      dispatch(getVehicleInfoActionByYmsVin(encodedVin, callbackfn));
    }
  }, [formDatas.vin, selectedOption, dispatch]);

  useEffect(() => {
    if (open && !editMode && coverages.length === 0) {
      setCoverages([{ type: "", limit1: null, limit2: null, deductible: "", premium: null }]);
    }
  }, [open, editMode, coverages.length]);

  useEffect(() => {
    if (Array.isArray(customerData?.vehicles?.content)) {
      setVehicles(customerData?.vehicles?.content);
      setVehiclesPagination(customerData?.vehicles);
    }
  }, [customerData]);

  const handleVinPopulate = async (value) => {
    try {
      const response = await dispatch(getVehicleInfoByVIN(value, ""));
      const data = response;
      if (data) {
        setNewVehicle((prev) => ({
          ...prev,
          year: data.year || data.modelYear || "",
          make: data.make || data.manufacturer || "",
          model: data.model || "",
          bodyType: data.bodyType || data.vehicleType || "",
          engineType: data.engineType || "",
          garaging: data.garaging || "",
          policyId: getvehicleid,
        }));

        setErrors((prev) => {
          const updated = { ...prev };
          delete updated.vin;
          if (data.year || data.modelYear) delete updated.year;
          if (data.make || data.manufacturer) delete updated.make;
          if (data.model) delete updated.model;
          if (data.bodyType || data.vehicleType) delete updated.bodyType;
          if (data.engineType) delete updated.engineType;
          if (data.garaging) delete updated.garaging;
          return updated;
        });
      } else {
        setNewVehicle((p) => ({
          ...p, year: "", make: "", model: "", bodyType: "", engineType: "", garaging: ""
        }));
        setErrors((prev) => ({ ...prev, vin: "Invalid VIN" }));
      }
    } catch {
      setNewVehicle((p) => ({
        ...p, year: "", make: "", model: "", bodyType: "", engineType: "", garaging: ""
      }));
      setErrors((prev) => ({ ...prev, vin: "Invalid VIN" }));
    }
  };

  const resetForm = () => {
    setFormDatas({
      modelYear: "",
      manufacturer: "",
      model: "",
      vehBodyType: "",
      vin: "",
      zipcode: "",
      garaging: "",
    });
    setNewVehicle({
      vin: "",
      year: "",
      make: "",
      model: "",
      bodyType: "",
      engineType: "",
      garaging: "",
    });
    setCoverages([{ type: "", limit1: null, limit2: null, deductible: "", premium: null }]);
    setErrors({});
    setCoverageError("");
    setSubmitError("");
    setRowErrors({});
  };

  const formatTwoDecimals = (val) => {
    const n = Number(val);
    if (Number.isNaN(n)) return "";
    return n.toFixed(2);
  };

  const handlePremiumChange = (index, raw) => {
    let s = String(raw ?? "");
    s = s.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
    let [intPart, decPart = ""] = s.split(".");
    intPart = intPart.slice(0, 6);
    decPart = decPart.slice(0, 2);
    s = decPart ? `${intPart}.${decPart}` : intPart;

    setCoverages((prev) => prev.map((c, i) => (i === index ? { ...c, premium: s } : c)));

    setRowErrors((prev) => {
      const t = (coverages[index]?.type || "").trim();
      const next = { ...prev };
      if (s && !t) {
        next[index] = { ...(next[index] || {}), type: "Select Type first" };
      } else if (next[index]?.type) {
        const { type, ...rest } = next[index];
        if (Object.keys(rest).length) next[index] = rest;
        else delete next[index];
      }
      if (s && !/^\d{1,6}(\.\d{1,2})?$/.test(s)) {
        next[index] = { ...(next[index] || {}), premium: "Max 2 decimals (e.g., 123.45)" };
      } else if (next[index]?.premium) {
        const { premium, ...rest } = next[index];
        next[index] = Object.keys(rest).length ? rest : undefined;
        if (!next[index]) delete next[index];
      }
      return next;
    });
  };

const getPrefillCoverages = () => {
  const srcVeh = Array.isArray(vehicles) && vehicles.length ? vehicles[0] : null;
  const list = srcVeh?.coverages?.content || [];
  if (!list.length) return null;

  return list.map((coverage) => {
    const ui = inflateCoverageForUI(policyState, {
      id: undefined,
      type: coverage.type || "",
      limit1: coverage.limit1 ?? null,
      limit2: coverage.limit2 ?? null,
      deductible: coverage.deductible ?? "",
      // premium from last time is intentionally ignored
      premium: null,
      limitType: coverage.limitType,
    });

    // Always blank the premium for a fresh add-vehicle flow
    return { ...ui, premium: "" };
  });
};


  const validateRow = (c) => {
    const errs = {};
    const t = normType(c.type);

    const hasAnyValue =
      t ||
      (c.limit1 !== null && c.limit1 !== "" && c.limit1 !== undefined) ||
      (c.limit2 !== null && c.limit2 !== "" && c.limit2 !== undefined) ||
      (c.deductible !== null && c.deductible !== "" && c.deductible !== undefined) ||
      (c.premium !== null && c.premium !== "" && c.premium !== undefined);

    if (!hasAnyValue) return errs;

    if (!t) errs.type = "Select Type first";
    if (!t) return errs;

    const isTwoLimit = t === "bi" || t === "umbi";
    const isOneLimit = t === "pd" || t === "pip" || t === "medpay" || t === "tow" || t === "rental";
    const isDedOnly = t === "comp" || t === "coll" ;
    const noCovg = coverageHasNoCovg(c);

    if (!noCovg) {
      if (isTwoLimit) {
        if (!c.limit1) errs.limit1 = "Required";
        if (!c.limit2) errs.limit2 = "Required";
      } else if (isOneLimit) {
        if (!c.limit1) errs.limit1 = "Required";
      } else if (isDedOnly) {
        if (!c.deductible) errs.deductible = "Required";
      }
    }

    if (!noCovg) {
      if (c.premium === "" || c.premium == null) {
        errs.premium = "Premium is required";
      } else if (!/^\d{1,6}(\.\d{1,2})?$/.test(String(c.premium))) {
        errs.premium = "Use up to 2 decimals (e.g., 123.45)";
      }
    }

    return errs;
  };

  const handleSaveVehicle = async () => {
    const isVinType = selectedOption === "vin";
    const newErrors = {};
    setCoverageError("");
    setSubmitError("");
    setRowErrors({});

    if (hasPipMedpayConflict(coverages)) {
      setCoverageError("You can choose only one from PIP and MEDPAY.");
      return;
    }

    if (isVinType) {
      ["vin", "year", "make", "model", "bodyType", "garaging"].forEach((f) => {
        if (!newVehicle[f]) newErrors[f] = f.charAt(0).toUpperCase() + f.slice(1) + " is required";
      });
    } else {
      ["modelYear", "manufacturer", "model", "vehBodyType", "vin", "garaging"].forEach((f) => {
        if (!formDatas[f]) newErrors[f] = f.charAt(0).toUpperCase() + f.slice(1) + " is required";
      });
    }

    const tmpRowErrors = {};
    coverages.forEach((c, idx) => {
      const errs = validateRow(c);
      if (Object.keys(errs).length) tmpRowErrors[idx] = errs;
    });

    if (Object.keys(newErrors).length > 0 || Object.keys(tmpRowErrors).length > 0) {
      setErrors(newErrors);
      if (Object.keys(tmpRowErrors).length > 0) {
        setRowErrors(tmpRowErrors);
        setSubmitError("Please fix the highlighted coverage rows.");
      }
      return;
    }

    // Require at least one real coverage when adding a vehicle
    if (!editMode && !hasAtLeastOneRealCoverage(policyState, coverages)) {
      setCoverageError("Please add at least one coverage (not 'No Covg') before saving.");
      // Nudge the user by flagging the first row’s type if it’s empty
      setRowErrors((prev) => {
        const next = { ...prev };
        if (!coverages[0]?.type) next[0] = { ...(next[0] || {}), type: "Select Type" };
        return next;
      });
      return; // keep the modal open and stop submit
    }

  const coveragesForPayload = normalizeCoverages(policyState, coverages)
    .filter((coverage) => coverage.type && String(coverage.type).trim() !== "")
    .map((coverage) => ({
      id: editMode ? coverage.id : undefined,
      type: String(coverage.type || "").toUpperCase(),
      limitType: coverage.limitType,
      limit1: coverage.limit1,
      limit2: coverage.limit2,
      deductible: coverage.deductible,
      premium:
        coverage.premium === "" || coverage.premium == null
          ? 0
          : parseFloat(Number(coverage.premium).toFixed(2)),
    }));

    const payload = {
      ...newVehicle,
      id: editMode ? (selectedOption === "vin" ? newVehicle.id : formDatas.id) : undefined,
      policyId: policyId || getvehicleid,
      savedBy: selectedOption,
      coverages: coveragesForPayload,
    };

    if (!isVinType) {
      Object.assign(payload, {
        year: formDatas.modelYear,
        make: formDatas.manufacturer,
        model: formDatas.model,
        bodyType: formDatas.vehBodyType,
        vin: formDatas.vin,
        garaging: formDatas.garaging,
      });
    }

    try {
      if (editMode) {
        await dispatch(VehicleUpdateAction(payload));
      } else {
        await dispatch(saveVehicleAction(payload));
      }
      dispatch(getDetailPolicyPaginationAction(policyId));
      dispatch(getCoverageListingAction(policyId));
      
      const page0 = vehiclesPageObj?.number ?? 0;
      
      resetForm();
      setOpen(false);
      setEditMode(false);
    } catch (error) {
      console.error("❌ Error saving vehicle:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const validationRules = {
      model:     { pattern: /^[A-Za-z0-9\s\-()]*$/, maxLength: 50 },
      make:      { pattern: /^[A-Za-z0-9\s\-()]*$/, maxLength: 50 },
      bodyType:  { pattern: /^[A-Za-z0-9\s\-()]*$/, maxLength: 80 },
      engineType:{ pattern: /^[A-Za-z0-9\s\-()]*$/, maxLength: 50 },
      garaging:  { pattern: /^[A-Za-z0-9]*$/,         maxLength: 5 },
      vin:       { pattern: /^[A-Za-z0-9]*$/,         maxLength: 30 },
      year:      { pattern: /^[0-9]*$/,               maxLength: 4  },
    };

    const rule = validationRules[name];
    if (rule && (!rule.pattern.test(value) || value.length > rule.maxLength)) return;

    setNewVehicle((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "vin") {
      if (value.length >= 17) {
        handleVinPopulate(value);
      } else {
        setNewVehicle((p) => ({ ...p, year: "", make: "", model: "", bodyType: "", engineType: "", garaging: "" }));
      }
    }
  };

  const handleCoverageChange = (index, field, value) => {
    setCoverages((prev) => {
      let next = prev.map((coverage, i) =>
        i === index ? { ...coverage, [field]: value } : coverage
      );

      const row = next[index];

      if (["bi", "umbi"].includes((row.type || "").toLowerCase())) {
        if (field === "limit1" && isNoCovg(value)) {
          next[index] = { ...row, limit1: NO_COVG_VALUE, limit2: NO_COVG_VALUE, premium: "" };
        } else if (field === "limit2" && isNoCovg(value)) {
          next[index] = { ...row, limit1: NO_COVG_VALUE, limit2: NO_COVG_VALUE, premium: "" };
        }
      }

      if (["limit1", "limit2", "deductible"].includes(field) && coverageHasNoCovg(next[index])) {
        next[index] = { ...next[index], premium: "" };
      }

      const types = next.map((c) => (c.type || "").toLowerCase());
      const compIdx = types.indexOf("comp");
      const collIdx = types.indexOf("coll");

      if (["comp", "coll"].includes((row.type || "").toLowerCase()) && field === "deductible") {
        const otherIdx = (row.type || "").toLowerCase() === "comp" ? collIdx : compIdx;
        if (otherIdx !== -1) next[otherIdx] = { ...next[otherIdx], deductible: value };
      }

      if (field === "type" && (value === "comp" || value === "coll")) {
        const otherIdx = value === "comp" ? collIdx : compIdx;
        if (otherIdx !== -1) {
          const otherDed = next[otherIdx]?.deductible;
          if (otherDed != null && otherDed !== "") next[index] = { ...next[index], deductible: otherDed };
        }
      }

      if (coverageError && !hasPipMedpayConflict(next)) setCoverageError("");

      return next;
    });

    setRowErrors((prev) => {
      const next = { ...prev };
      const row = next[index];
      if (!row) return next;

      if (row[field]) {
        const { [field]: _removed, ...rest } = row;
        if (Object.keys(rest).length) next[index] = rest;
        else delete next[index];
      }
      if (field === "type" && row?.type) {
        const { type, ...rest } = row;
        if (Object.keys(rest).length) next[index] = rest;
        else delete next[index];
      }
      return next;
    });
  };

  const handleAddCoverage = () => {
    setCoverages((prev) => [
      ...prev,
      { type: "", limit1: null, limit2: null, deductible: "", premium: null },
    ]);
  };

  const handleDeleteCoverage = (index) => {
    if (coverages.length === 1) return;
    setCoverages((prev) => prev.filter((_, i) => i !== index));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleCancel = () => {
    setNewVehicle(initialVehicleState);
    resetForm();
    setSelectedOption("vin");
    setErrors({});
    setOpen(false);
  };

const handleChangePage = async (_e, newPage1) => {
  const page0 = newPage1 - 1;
  await dispatch(getDetailPolicyPaginationAction(policyId, { section: 'vehicle', page: page0, size: rowsPerPage }));
};

const handleChangeRowsPerPage = async (e) => {
  const newSize = parseInt(e.target.value, 10);
  setRowsPerPage(newSize); // useEffect above refetches page 0
};

  const handleEditVehicle = async (vehicleId) => {
    setEditMode(true);
    setSelectedVehicleId(vehicleId);
    setOpen(true);
    const selectedVehicle = vehicles?.find((vehicle) => vehicle.id === vehicleId);
    if (selectedVehicle) {
      setSelectedOption((selectedVehicle?.savedBy || "").toLowerCase());
      if ((selectedVehicle?.savedBy || "").toLowerCase() === "vin") {
        setNewVehicle({
          id: selectedVehicle.id,
          vin: selectedVehicle.vin || "",
          make: selectedVehicle.make || "",
          year: selectedVehicle.year || "",
          model: selectedVehicle.model || "",
          bodyType: selectedVehicle.bodyType || "",
          garaging: selectedVehicle.garaging || "",
          policyId: getvehicleid,
        });
      } else {
        setFormDatas({
          id: vehicleId,
          modelYear: selectedVehicle.year || "",
          manufacturer: selectedVehicle.make || "",
          model: selectedVehicle.model || "",
          vehBodyType: selectedVehicle.bodyType || "",
          vin: selectedVehicle.vin || "",
          zipcode: selectedVehicle.zipcode || "",
          garaging: selectedVehicle.garaging || "",
          policyId: getvehicleid,
        });
      }
      const formattedCoverages =
        selectedVehicle.coverages?.content.map((coverage) =>
          inflateCoverageForUI(policyState, {
            id: coverage.id,
            type: coverage.type || "",
            limit1: coverage.limit1 ?? null,
            limit2: coverage.limit2 ?? null,
            deductible: coverage.deductible ?? "",
            premium: coverage.premium ?? 0,
            limitType: coverage.limitType,
          })
        ) || [];
      setCoverages(formattedCoverages);
      setCoverageError("");
      setRowErrors({});
      setSubmitError("");
    }
    dispatch(getPolicyPremiumDetails(policyId));
  };

  const handleOptionChange = (event) => {
    const selected = event.target.value;
    setSelectedOption(selected);
    if (selected === "vin") {
      setErrors((prev) => {
        const updated = { ...prev };
        Object.keys(formDatas).forEach((f) => delete updated[f]);
        return updated;
      });
    }
  };

  const handleVechileId = (id) => {
    const selectedVechile = vehicles.find((vehicle) => vehicle.id === id);
    if (selectedVechile) {
      setVehicleDetail(selectedVechile);
      setVechicleModel(true);
    }
  };

  const paginatedRows = vehicles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDeleteClick = async (id) => {
    await setDeletedVehicle(id);
    await setIsDialogOpen(true);
  };

  const confirmDeleteVechile = async () => {
    await dispatch(deleteVechileAction(deletedVechile));
    await dispatch(getDetailPolicyPaginationAction(policyId, { section: 'vehicle', page: 0, size: rowsPerPage }));
    await setIsDialogOpen(false);
  }

  const handleCancelDelete = () => {
    setIsDialogOpen(false);
  };

  return (
    <div>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow className="styled-table-row">
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="transaction" color="black">No.</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="transaction" color="black">Year</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="entrydate" color="black">Make</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="effdate" color="black">Model</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">VIN</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">Body Type</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">COMP-DED</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">COL-DED</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">TOW-DED</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">RENTAL-DED</Typography>
              </TableCell>
              <TableCell className="styled-table-heading">
                <Typography id="amount" color="black">Garaging Zip code</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">Detail</Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography id="amount" color="black">Action</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((vehicle, index) => {
              const compDeductible   = vehicle.coverages?.content.find(c => (c.type || "").toLowerCase() === "comp")?.deductible || "-";
              const collDeductible   = vehicle.coverages?.content.find(c => (c.type || "").toLowerCase() === "coll")?.deductible || "-";
              const towDeductible    = vehicle.coverages?.content.find(c => (c.type || "").toLowerCase() === "tow")?.deductible || "-";
              const rentalDeductible = vehicle.coverages?.content.find(c => (c.type || "").toLowerCase() === "rental")?.deductible || "-";

              return (
                <TableRow className="styled-table-row" key={vehicle.id}>
                  {/* serial number based on server page */}
                  <TableCell>#{backendPage0 * rowsPerPage + (index + 1)}</TableCell>
                  <TableCell>{vehicle.year}</TableCell>
                  <TableCell>{vehicle.make}</TableCell>
                  <TableCell>{vehicle.model}</TableCell>
                  <TableCell>{vehicle.vin}</TableCell>
                  <TableCell>{vehicle.bodyType}</TableCell>
                  <TableCell>{compDeductible}</TableCell>
                  <TableCell>{collDeductible}</TableCell>
                  <TableCell>{towDeductible}</TableCell>
                  <TableCell>{rentalDeductible}</TableCell>
                  <TableCell>{vehicle.garaging}</TableCell>
                  <TableCell>
                    <Button onClick={() => handleVechileId(vehicle.id)}>Detail</Button>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditVehicle(vehicle.id)} style={{ marginLeft: "auto" }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                    >
                      <DeleteIcon onClick={() => handleDeleteClick(vehicle.id)} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

          </TableBody>
        </Table>
      </TableContainer>

    {vehiclesPagination?.totalElements > 0 && (
  <Box sx={{ width: "100%", overflow: "hidden", padding: "16px 0" }}>
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={2}
    >
      <Box display="flex" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: "8px" }}>
          Rows per page:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          size="small"
          sx={{
            height: '36px',
            minWidth: '80px',
            '& .MuiSelect-select': { padding: '8px 32px 8px 12px' }
          }}
        >
          {[5, 10, 15, 25, 50, 100].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>

       <Pagination
          count={Math.ceil(vehiclesPagination.totalElements / rowsPerPage)}
          page={currentPage1}            // see note below
          onChange={handleChangePage}    // pass 1-based value straight through
          showFirstButton
          showLastButton
        />
    </Stack>
  </Box>
)}


<Box sx={{ display: "flex", justifyContent: "end", mt: 2, mb: 2 }}>
        <Button
          variant="outlined"
          id="Addvehiclebutton"
          onClick={() => {
          setOpen(true);
          setEditMode(false);
          setNewVehicle({ ...initialVehicleState, garaging: garagingZipCode || "" });
          setFormDatas((prev) => ({ ...prev, garaging: garagingZipCode || "" }));
          const pre = getPrefillCoverages();
          setCoverages(pre && pre.length ? pre : [
            { type: "", limit1: null, limit2: null, deductible: "", premium: null }
          ]);
          setCoverageError("");
          setSubmitError("");
          setRowErrors({});
        }}

        >
          Add Vehicle
        </Button>
      </Box>

      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={(event, reason) => {
          // prevent closing when user clicks outside or presses Esc
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setOpen(false);
        }}
        disableScrollLock
        keepMounted={false}
        hideBackdrop={false}
        style={{ zIndex: 1300 }}
      >
        <DialogTitle id="addvehicletitle">
          {editMode ? "Update Vehicle" : "Add Vehicle"}
        </DialogTitle>

        <DialogContent sx={{ margin: "20px 0 0 0" }}>
          <FormControl component="fieldset">
            <RadioGroup row value={selectedOption} onChange={handleOptionChange}>
              <FormControlLabel value="vin" control={<Radio />} label="VIN" />
              <FormControlLabel value="yms" control={<Radio />} label="YMS" />
            </RadioGroup>
          </FormControl>

          {selectedOption === "vin" ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {Object.keys(newVehicle)
                .filter(
                  (key) =>
                    key !== "driverId" &&
                    key !== "policyId" &&
                    key !== "engineType" &&
                    (key !== "id" || !editMode)
                )
                .map((key) => (
                  <Grid item xs={12} sm={3} key={key}>
                    <Typography>
                      <label className="fnt-we">
                        {key === "garaging"
                          ? "Garaging Zip code"
                          : key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                      </label>
                    </Typography>
                    <TextField
                      fullWidth
                      name={key}
                      value={newVehicle[key]}
                      onChange={handleChange}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                  </Grid>
                ))}
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={3}>
                <label className="fnt-we">Model Year</label>
                <TextField
                  name="modelYear"
                  value={formDatas.modelYear}
                  onChange={handleInputChange}
                  select
                  fullWidth
                  required
                  error={!!errors.modelYear}
                  helperText={errors.modelYear}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <label className="fnt-we">Make</label>
                <TextField
                  name="manufacturer"
                  value={formDatas.manufacturer}
                  onChange={handleInputChange}
                  select
                  fullWidth
                  required
                  disabled={!formDatas.modelYear}
                  error={!!errors.manufacturer}
                  helperText={errors.manufacturer}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {Make.map((make) => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <label className="fnt-we">Model</label>
                <TextField
                  name="model"
                  value={formDatas.model}
                  onChange={handleInputChange}
                  select
                  fullWidth
                  required
                  disabled={!formDatas.manufacturer}
                  error={!!errors.model}
                  helperText={errors.model}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {models.map((model) => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <label className="fnt-we">Body Type</label>
                <TextField
                  name="vehBodyType"
                  value={formDatas.vehBodyType}
                  onChange={handleInputChange}
                  select
                  fullWidth
                  required
                  disabled={!formDatas.model}
                  error={!!errors.vehBodyType}
                  helperText={errors.vehBodyType}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {vehBodyType.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <label className="fnt-we">VIN</label>
                <TextField
                  name="vin"
                  value={formDatas.vin}
                  onChange={handleInputChange}
                  select
                  fullWidth
                  required
                  disabled={!formDatas.vehBodyType}
                  error={!!errors.vin}
                  helperText={errors.vin}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {vins.map((vin) => (
                    <option key={vin} value={vin}>{vin}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <label className="fnt-we">Garaging Zip code</label>
                <TextField
                  name="garaging"
                  value={formDatas.garaging}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  error={!!errors.garaging}
                  helperText={errors.garaging}
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mt: 2, padding: "0 18px" }}>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.5, margin: "10px 0 0 0" }}>
              <h3>Add Coverage</h3>
              <Typography variant="caption" sx={{ color: "#666" }}>
                Note: You can add only <strong>one</strong> cover from <strong>PIP</strong> and <strong>MEDPAY</strong>.
              </Typography>
              {!!coverageError && (
                <Typography variant="body2" sx={{ color: "error.main", mt: 0.5 }}>
                  {coverageError}
                </Typography>
              )}
              {!!submitError && (
                <Typography variant="body2" sx={{ color: "error.main", mt: 0.5 }}>
                  {submitError}
                </Typography>
              )}
            </Box>

            <Grid container spacing={2}>
              {coverages.map((coverage, index) => {
                const tKey = (coverage.type || "").toLowerCase();

                return (
                  <Grid container spacing={2} sx={{ mt: 1, padding: "0 18px" }} key={index}>
                    <Grid item xs={12} sm={3}>
                      <Typography><label className="fnt-we">Select Type</label></Typography>
                      <FormControl fullWidth error={!!rowErrors[index]?.type}>
                        <Select
                          value={coverage.type || ""}
                          onChange={(e) => handleCoverageChange(index, "type", e.target.value)}
                          displayEmpty
                          renderValue={(selected) =>
                            selected
                              ? String(selected).toUpperCase()
                              : <em style={{ color: "#888" }}>Select Type</em>
                          }
                        >
                          <MenuItem value="">
                            <em>Select Type</em>
                          </MenuItem>
                          {getTypeMenuOptions(coverages, index).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                              {opt.value.toUpperCase()}
                            </MenuItem>
                          ))}
                        </Select>
                        {!!rowErrors[index]?.type && (
                          <FormHelperText>{rowErrors[index].type}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    {["bi", "pd", "umbi", "pip", "medpay", "tow", "rental"].includes(tKey) && (
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth error={!!rowErrors[index]?.limit1}>
                          <Typography><label className="fnt-we">Limit 1</label></Typography>
                          <Select
                            native
                            value={coverage.limit1 ?? ""}
                            onChange={(e) => handleCoverageChange(index, "limit1", e.target.value)}
                            inputProps={{ id: "limit1-native" }}
                          >
                            <option value="">Select Limit</option>
                            {buildOptions(
                              limitOptions[tKey]?.limit1 || [],
                              ["bi", "pd", "umbi", "pip"].includes(tKey)
                            ).map((option, idx) => (
                              <option key={`${normalizeKey(option.value)}-${idx}`} value={String(option.value)}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                          {!!rowErrors[index]?.limit1 && (
                            <FormHelperText>{rowErrors[index].limit1}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {["bi", "umbi"].includes(tKey) && (
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth error={!!rowErrors[index]?.limit2}>
                          <Typography><label className="fnt-we">Limit 2</label></Typography>
                          <Select
                            native
                            value={coverage.limit2 ?? ""}
                            onChange={(e) => handleCoverageChange(index, "limit2", e.target.value)}
                            inputProps={{ id: "limit2-native" }}
                          >
                            <option value="">Select Limit</option>
                            {buildOptions(limitOptions[tKey]?.limit2 || [], true).map((option, idx) => (
                              <option key={`${normalizeKey(option.value)}-${idx}`} value={String(option.value)}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                          {!!rowErrors[index]?.limit2 && (
                            <FormHelperText>{rowErrors[index].limit2}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {["comp", "coll"].includes(tKey) && (
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth error={!!rowErrors[index]?.deductible}>
                          <Typography><label className="fnt-we">Deductible</label></Typography>
                          <Select
                            value={coverage.deductible ?? ""}
                            onChange={(e) => handleCoverageChange(index, "deductible", e.target.value)}
                          >
                            {buildOptions(limitOptions[tKey]?.deductible || []).map((option, idx) => (
                              <MenuItem key={`${normalizeKey(option.value)}-${idx}`} value={String(option.value)}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {!!rowErrors[index]?.deductible && (
                            <FormHelperText>{rowErrors[index].deductible}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {!coverageHasNoCovg(coverage) && (
                      <Grid item xs={12} sm={2}>
                        <Typography><label className="fnt-we">Premium</label></Typography>
                        <TextField
                          fullWidth
                          type="text"
                          value={coverage.premium ?? ""}
                          onChange={(e) => handlePremiumChange(index, e.target.value)}
                          onFocus={() => {
                            setCoverages((prev) =>
                              prev.map((c, i) =>
                                i === index && (c.premium == null || c.premium === "")
                                  ? { ...c, premium: "1.00" }
                                  : c
                              )
                            );
                          }}
                          onBlur={() => {
                            setCoverages((prev) =>
                              prev.map((c, i) =>
                                i === index && c.premium !== "" && c.premium != null
                                  ? { ...c, premium: formatTwoDecimals(c.premium) }
                                  : c
                              )
                            );
                          }}
                          inputProps={{
                            inputMode: "decimal",
                            pattern: "^\\d{1,6}(\\.\\d{1,2})?$",
                          }}
                          error={!!rowErrors[index]?.premium}
                          helperText={rowErrors[index]?.premium}
                        />
                      </Grid>
                    )}

                    <Grid item sx={{ display: "flex", justifyContent: "center", alignItems: "center" ,marginBottom: "-20px"}}>
                      {coverages.length > 1 && (
                        <DeleteOutlineIcon
                          onClick={() => { if (coverages.length > 1) handleDeleteCoverage(index); }}
                          style={{
                            color: coverages.length <= 1 ? "grey" : "inherit",
                            cursor: coverages.length <= 1 ? "not-allowed" : "pointer",
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>

            {(() => {
              const canAdd = getRemainingTypesForNewRow(coverages).length > 0;
              return (
                <Button
                  onClick={handleAddCoverage}
                  sx={{marginTop:'10px'}}
                  variant='outlined' size='large'
                  disabled={!canAdd}
                >
                  Add More
                </Button>
              );
            })()}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} id="cancelbutton" variant='outlined' size='large'>
            Cancel
          </Button>
          <Button onClick={handleSaveVehicle} id="addbutton" variant='outlined' size='large'>
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCancelDelete(event, reason);
        }}
        maxWidth="sm"
        fullWidth={isMobile}
        disableScrollLock={true}
        keepMounted={false}
        hideBackdrop={false}
        style={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            margin: isMobile ? 1 : 3,
            width: isMobile ? 'calc(100% - 16px)' : 'auto'
          }
        }}
      >
              <DialogTitle>
                Confirm Deletion
              </DialogTitle>
              <DialogContent>
                <DialogContentText sx={{
                  marginTop: '15px',
                  fontSize: isMobile ? '14px' : '16px',

                }}>
                  You are about to <strong>permanently delete</strong> this vehicle and all of its associated insurance coverages. This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{
                padding: isMobile ? '12px 16px' : '16px 24px',
                gap: 1
              }}>
                <Button
                  onClick={handleCancelDelete}                  
                  variant='outlined' size='large'
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteVechile}
                  variant='outlined' size='large'
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {getVehicleDetail && (
              <PolicyVechileDetails
                vehicleDetails={getVehicleDetail}
                vehicleModel={vehicleModel}
                setVechicleModel={setVechicleModel}
              />
            )}
    </div>
  );
};

export default PolicyVehicle;

